import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap, withLatestFrom, exhaustMap, concatMap, exhaust } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import * as ordersActions from '../store/actions/orders.action';
import { OrdersServerService } from '../services/orders.service';
import { EMPTY, of } from 'rxjs';
import { Order } from '../models/orders.models';
import * as ordersSelector from '../store/selectors/orders.selector';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { CalculatedRouteModalComponent } from 'src/app/features/orders-create/components/calculated-route-modal/calculated-route-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MatDialogRef } from '@angular/material/dialog';
import { CalculatedRouteModalService } from 'src/app/features/orders-create/components/calculated-route-modal/calculated-route-modal.service';
import * as chatActions from '../../chat/store/actions/chat.action';
import * as authActions from '../../auth/store/actions/auth.action';
import * as filterActions from 'src/app/core/filters/store/actions/filter.action';
import * as filterSelectors from 'src/app/core/filters/store/selectors/filter.selector';
import * as ridersActions from '../../riders/store/actions/riders.action';

import { Location } from '@angular/common';

@Injectable()
export class OrdersEffects {
  private dialogRef: MatDialogRef<CalculatedRouteModalComponent>;
    constructor(
        private actions$: Actions,
        private ordersService: OrdersServerService,
        private store: Store<{ order: Order }>,
        private toastr: ToastrService,
        private router: Router,
        private dialog: MatDialog,
        private calculatedRoute: CalculatedRouteModalService,
         private location: Location
    ) {}

    loadOrders$ = createEffect(() => this.actions$.pipe(
      ofType(ordersActions.loadOrders),
      withLatestFrom(this.store.pipe(select(filterSelectors.selectAllFilters))),
      exhaustMap(([action, filters]) => {
        return this.ordersService.getOrders(filters).pipe(
          map(orders => ordersActions.loadOrdersSuccess({ orders })),
          catchError((error) => {
            if (error.status === 401) {
              this.toastr.error('Usuario no autenticado');
              return of(authActions.logout());
            }
            this.toastr.error('No se pudieron cargar los servicios');
            return of(ordersActions.loadOrdersFailure({ error }));
          })
        );
      })
    ));
    

    loadManualOrders$ = createEffect(() => this.actions$.pipe(
      ofType(ordersActions.loadManualOrders),
      exhaustMap((action) =>
        this.ordersService.getManualOrders(action.page, action.limit, action.query, action.from, action.to)
        .pipe(
          map(orders => ordersActions.loadManualOrdersSuccess({ orders })),
          catchError((error) => {
            if (error.status === 401) {
              this.toastr.error('Usuario no autenticado');
              return of(authActions.logout());
            }
            this.toastr.error('No se pudieron cargar los servicios manuales');
            return of(ordersActions.loadManualOrdersFailure({ error }));
          })
        ))
      )
    );

  loadOrderById$ = createEffect(() => this.actions$.pipe(
    ofType(ordersActions.loadOrderById),
    exhaustMap((action) =>
      this.ordersService.getOrderById(action.id)
      .pipe(
        map(orders => ordersActions.loadOrderByIdSuccess({ orders, id: action.id })),
        catchError((error) => {
          if(error.status === 401) {
            this.toastr.error('Usuario no autenticado');
            return of(authActions.logout());
          } 
          this.toastr.error('No se pudo cargar el servicio');
          return of(ordersActions.loadOrderByIdFailure({ error }));
        })
      ))
    )
  );

    loadPendingOrders$ = createEffect(() => this.actions$.pipe(
      ofType(ordersActions.loadPendingOrders),
      exhaustMap((action) =>
        this.ordersService.getPendingOrders(action.page, action.limit, action.query)
        .pipe(
          map(orders => {
          
            return ordersActions.loadPendingOrdersSuccess({ orders });
          }),
          catchError((error) => {
            if(error.status === 401) {
              this.toastr.error('Usuario no autenticado');
              return of(authActions.logout());
            } 
            this.toastr.error('No se pudieron cargar los servicios pendientes');
            return of(ordersActions.loadPendingOrdersFailure({ error }));
          })
        ))
      )
    );

    loadOrderByRiderId$ = createEffect(() => this.actions$.pipe(
      ofType(ordersActions.loadOrdersByRiderId),
      exhaustMap((action) =>
        this.ordersService.getOrderByRiderId(action.rider_id, action.page, action.limit)
        .pipe(
          map(orders => {
          
             return ordersActions.loadOrdersByRiderIdSuccess({ orders })
            }
            ),
          catchError((error) => {
            if(error.status === 401) {
              this.toastr.error('Usuario no autenticado');
              return of(authActions.logout());
            } 
            this.toastr.error('No se pudieron cargar los servicios por el ID del repartidor');
            return of(ordersActions.loadOrdersByRiderIdFailure({ error }));
          })
        ))
      )
    );


    loadInProgressOrders$ = createEffect(() => this.actions$.pipe(
      ofType(ordersActions.loadInProgressOrders),
      exhaustMap((action) =>
        this.ordersService.getInProgressOrders(action.page, action.limit, action.query)
        .pipe(
          map(orders => ordersActions.loadInProgressOrdersSuccess({ orders })),
          catchError((error) => {
            if(error.status === 401) {
              this.toastr.error('Usuario no autenticado');
              return of(authActions.logout());
            } 
            this.toastr.error('No se pudieron cargar los servicios en progreso');
            console.error('Error loading in progress orders', error);
            return of(ordersActions.loadInProgressOrdersFailure({ error }));
          })
        ))
      )
    );

    loadFinishedOrders$ = createEffect(() => this.actions$.pipe(
      ofType(ordersActions.loadFinishedOrders),
      exhaustMap((action) =>
        this.ordersService.getFinishedOrders(action.page, action.limit, action.query)
        .pipe(
          map(orders => ordersActions.loadFinishedOrdersSuccess({ orders })),
          catchError((error) => {
            if(error.status === 401) {
              this.toastr.error('Usuario no autenticado');
              return of(authActions.logout());
            } 
            this.toastr.error('No se pudieron cargar los servicios finalizados');
            console.error('Error loading finished orders', error);
            return of(ordersActions.loadFinishedOrdersFailure({ error }));
          })
        ))
      )
    );

    loadFinishedOrdersWithCashToCollet$ = createEffect(() => this.actions$.pipe(
      ofType(ordersActions.loadFinishedOrdersWithCashToCollect),
      exhaustMap((action) =>
        this.ordersService.getPendingOrdersWithCashToCollet(action.page, action.limit, action.query)
        .pipe(
          map(orders => {
          
            return ordersActions.loadFinishedOrdersWithCashToCollectSuccess({ orders })
          } ),
          catchError((error) => {
            if(error.status === 401) {
              this.toastr.error('Usuario no autenticado');
              return of(authActions.logout());
            } 
            this.toastr.error('No se pudieron cargar los servicios finalizados');
            console.error('Error loading finished orders', error);
            return of(ordersActions.loadFinishedOrdersWithCashToCollectFailure({ error }));
          })
        ))
      )
    );


  updateOrder$ = createEffect(() => this.actions$.pipe(
    ofType(ordersActions.updateOrder),
    tap(() => {
      this.store.select(ordersSelector.selectOrder).subscribe(order => {
        console.log('Order updated', order);
      });
    }),
    catchError(() => EMPTY)
  ), { dispatch: false });
 
  calculateOrderRoute$ = createEffect(() => this.actions$.pipe(
    ofType(ordersActions.calculateOrderRoute),
  
    withLatestFrom(this.store.pipe(select(ordersSelector.selectOrder))),
    mergeMap(([action, order]) => {
      return this.ordersService.calculateRoute(order).pipe(
       
        map(calculatedOrderRoute => ordersActions.calculateOrderRouteSuccess({ calculatedOrderRoute })),
        catchError(error => {
          this.toastr.error('Eorror calculando la ruta');
          console.error('Server error:', error);
          return of(ordersActions.calculateOrderRouteFailure({ error }));
        })
      );
    })
  )); 

  loadOrderByChat$ = createEffect(() => this.actions$.pipe(
    ofType(chatActions.loadChat),
    withLatestFrom(this.store.pipe(select(ordersSelector.selectCurrentOrderId))),
    mergeMap(([action, orderId]) => {
      if (orderId) {
        return this.ordersService.getOrderById(orderId).pipe(
          map(orders => ordersActions.loadOrderByIdSuccess({ orders, id: orderId })),
          catchError(error => {
          
            return of(ordersActions.loadOrderByIdFailure({ error }));
          })
        );
      } else {
        console.error('No order ID found in chat');
        return of(ordersActions.loadOrderByIdFailure({ 
          error: 'No order ID found' 
        }));
      }
    })
  ));

  calculateOrderRouteFailure$ = createEffect(() => this.actions$.pipe(
    ofType(ordersActions.calculateOrderRouteFailure),
    tap(() => {
      this.calculatedRoute.closeDialog();
    })
  ), { dispatch: false });

  createOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ordersActions.createOrder),
      withLatestFrom(this.store.pipe(select(ordersSelector.selectOrder))),
      mergeMap(([action, order]) =>
        this.ordersService.createOrder(order).pipe(
          map(order => {
            this.toastr.success('Servicio creado exitosamente');
            return ordersActions.createOrderSuccess({ order });
          }),
          catchError(error => {
            this.toastr.error('Error creando el servicio, verficar los datos ingresados');
            return of(ordersActions.createOrderFailure({ error }));
          })
        )
      )
    )
  );

  createManualOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ordersActions.createManualOrder),
      withLatestFrom(this.store.pipe(select(ordersSelector.selectOrder))),
      mergeMap(([action, order]) =>
        this.ordersService.createManualOrder(order).pipe(
          map(order => {
            this.toastr.success('Servicio creado exitosamente');
            return ordersActions.createManualOrderSuccess({ order });
          }),
          catchError(error => {
            this.toastr.error('Error creando el servicio, verficar los datos ingresados');
            return of(ordersActions.createManualOrderFailure({ error }));
          })
        )
      )
    )
  );

  createManualOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ordersActions.createManualOrderSuccess),
      map(() => {
        this.router.navigate(['/upload-completed-orders']);
        return ordersActions.loadPendingOrders({ page: 1, limit: 10 });
      })
    )
  );

  createOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ordersActions.createOrderSuccess),
      map(() => {
        this.router.navigate(['/orders-assignement']);
        return ordersActions.loadPendingOrders({ page: 1, limit: 10 });
      })
    )
  );

  cancelOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ordersActions.cancelOrder),
      mergeMap(action =>
        this.ordersService.cancelOrder(action.order_id).pipe(
          map(order => { 
            this.toastr.success(`Servicio ${action.order_id} eliminado exitosamente`);
            return ordersActions.cancelOrderSuccess({ order }); 
          }),
          catchError(error => {
            this.toastr.error(`Error cancelando el servicio ${action.order_id}`);
            return of(ordersActions.cancelOrderFailure({ error }));
          })
        )
      )
    )
  );

  reloadOrdersAfterCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ordersActions.cancelOrderSuccess),
      withLatestFrom(
        this.store.pipe(select(ordersSelector.selectTotalItems)),
        this.store.pipe(select(ordersSelector.selectTotalPages))
      ),
      map(([action, totalItems, totalPages]) => {
        const page = 1; 
        const limit = 5; 
        return ordersActions.loadOrders();
      })
    )
  );

  

 assignOrderToRider$ = createEffect(() =>
  this.actions$.pipe(
    ofType(ordersActions.assignOrderToRider),
    mergeMap(action =>
      this.ordersService.assignOrderToRider(action.order_id, action.rider_id).pipe(
        mergeMap(order => {
          this.toastr.success(`Servicio ${action.order_id} asignado exitosamente`);
          const updatedOrder: Order = {
            ...order,
            service_request: order.service_request
          };
          return of(
            ordersActions.assignOrderToRiderSuccess({ order: updatedOrder }),
            ridersActions.markRiderAsBusy({ rider_id: action.rider_id })
          );
        }),
        catchError(error => {
          this.toastr.error(`Error asignando el servicio ${action.order_id}`);
          return of(ordersActions.assignOrderToRiderFailure({ error }));
        })
      )
    )
  )
);

  unassignOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ordersActions.reassignOrder),
      mergeMap(action =>
        this.ordersService.reassingOrder(action.order_id, action.rider_id).pipe(
          map(order => {
           
            this.toastr.success(`Servicio ${action.order_id} desasignado exitosamente`);
            return ordersActions.reassignOrderSuccess({ order_id: action.order_id, payload: order });
          }),
          catchError(error => {
            this.toastr.error('Error desasignando el servicio');
            return of(ordersActions.reassignOrderFailure({ error }));
          })
        )
      )
    )
  );

  reloadOrdersAfterReassign$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ordersActions.reassignOrderSuccess),
      withLatestFrom(
        this.store.pipe(select(ordersSelector.selectTotalItems)),
        this.store.pipe(select(ordersSelector.selectTotalPages))
      ),
      map(([action, totalItems, totalPages]) => {
        const page = 1; // Puedes ajustar esto según tus necesidades
        const limit = 5; // Puedes ajustar esto según tus necesidades
        return ordersActions.loadOrders();
      })
    )
  );

  editOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ordersActions.editOrder),
      withLatestFrom(this.store.pipe(select(ordersSelector.selectOrder))),
      mergeMap(([action, order]) =>
        this.ordersService.editOrder(order.id, order).pipe( // Asegúrate de que `action.id` exista y sea el ID correcto a pasar
          map(order => {
            this.toastr.success('Servicio editado exitosamente');
            this.location.back();
            return ordersActions.editOrderSuccess({ order });
          }),
          catchError(error => {
            this.toastr.error('Error editando el servicio, verificar los datos ingresados');
            return of(ordersActions.editOrderFailure({ error }));
          })
        )
      )
    )
  );

  confirmCashDelivered$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ordersActions.confirmCashDelivered),
      mergeMap(action =>
        this.ordersService.confirmCashDelivered(action.order_id, action.confirmed).pipe(
          map(order => {
           
            this.toastr.success('Efectivo confirmado exitosamente');
            return ordersActions.confirmCashDeliveredSuccess({ order });
          }),
          catchError(error => {
           
            this.toastr.error('Error confirmando el efectivo');
            return of(ordersActions.confirmCashDeliveredFailure({ error }));
          })
        )
      )
    )
  );

  loadVehicleTypes$ = createEffect(() => this.actions$.pipe(
    ofType(ordersActions.loadVehicleTypes),
    exhaustMap(() =>
      this.ordersService.getVehicleTypes().pipe(
        map(vehicleTypes => ordersActions.loadVehicleTypesSuccess({ vehicleTypes })),
        catchError(error => {
          this.toastr.error('Error cargando los tipos de vehículos');
          return of(ordersActions.loadVehicleTypesFailure({ error }));
        })
      ))
  ));

  loadManualOrdersByRiderId$ = createEffect(() => this.actions$.pipe(
    ofType(ordersActions.loadManualOrdersByRiderId),
    exhaustMap((action) =>
        this.ordersService.getManualOrderByRiderId(action.rider_id, action.page, action.limit, action.from, action.to)
        .pipe(
            map(orders => ordersActions.loadManualOrdersByRiderIdSuccess({ orders })),
            catchError((error) => of(ordersActions.loadManualOrdersByRiderIdFailure({ error })))
        )
    )
));

}
